<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalServiceCostAllocators',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    service: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );

  const act_2576027262358435_emit_nhh228a07_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2576027262358435_showModal_o7ep9p2eg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalServiceCostAllocator',
      props: {
        oid: $event.scope.item.id,
        service: props.service,
      },
    };
  };
  const act_7593494570053535_emit_izh3lbp88_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_7593494570053535_request_4xqt0p600_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_rental_service_cost_allocator',
      data: $event.scope.item,
    };
  };
  const act_7593494570053535_request_4xqt0p600_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_2140198529698299_showModal_m5hutr4cv_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalServiceCostAllocator',
      props: {
        service: props.service,
      },
    };
  };

  const act_2576027262358435_emit_nhh228a07 = new actionTypes.EmitAction({
    actionArgs: act_2576027262358435_emit_nhh228a07_getActionArgs,
  });
  const inst_evh_2576027262358435_close_02e275ta8 =
    new actionTypes.EventHandlerImpl({
      action: act_2576027262358435_emit_nhh228a07,
      event: 'close',
      displayName: 'emit',
    });
  const act_2576027262358435_showModal_o7ep9p2eg =
    new actionTypes.ShowModalAction({
      actionArgs: act_2576027262358435_showModal_o7ep9p2eg_getActionArgs,
      events: [inst_evh_2576027262358435_close_02e275ta8],
    });
  const inst_evh_2576027262358435_clickPreventStop_5ua0nmhy1 =
    new actionTypes.EventHandlerImpl({
      action: act_2576027262358435_showModal_o7ep9p2eg,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2576027262358435_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2576027262358435_clickPreventStop_5ua0nmhy1],
      event: 'click.prevent.stop',
    });
  const act_7593494570053535_emit_izh3lbp88 = new actionTypes.EmitAction({
    actionArgs: act_7593494570053535_emit_izh3lbp88_getActionArgs,
  });
  const inst_evh_7593494570053535_success_2tkb78ja0 =
    new actionTypes.EventHandlerImpl({
      action: act_7593494570053535_emit_izh3lbp88,
      event: 'success',
      displayName: 'emit',
    });
  const act_7593494570053535_request_4xqt0p600 = new actionTypes.RequestAction({
    actionArgs: act_7593494570053535_request_4xqt0p600_getActionArgs,
    confirm: act_7593494570053535_request_4xqt0p600_getConfirm,
    events: [inst_evh_7593494570053535_success_2tkb78ja0],
  });
  const inst_evh_7593494570053535_clickPreventStop_ic1b80zpb =
    new actionTypes.EventHandlerImpl({
      action: act_7593494570053535_request_4xqt0p600,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_7593494570053535_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7593494570053535_clickPreventStop_ic1b80zpb],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_showModal_m5hutr4cv =
    new actionTypes.ShowModalAction({
      actionArgs: act_2140198529698299_showModal_m5hutr4cv_getActionArgs,
    });
  const inst_evh_2140198529698299_clickPreventStop_mfj3eeatz =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_showModal_m5hutr4cv,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_mfj3eeatz],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_2576027262358435_close_02e275ta8,
    inst_evh_2576027262358435_clickPreventStop_5ua0nmhy1,
    inst_evh_2576027262358435_clickPreventStop,
    inst_evh_7593494570053535_success_2tkb78ja0,
    inst_evh_7593494570053535_clickPreventStop_ic1b80zpb,
    inst_evh_7593494570053535_clickPreventStop,
    inst_evh_2140198529698299_clickPreventStop_mfj3eeatz,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_2576027262358435_close_02e275ta8 = async (event, scope) => {
    return await actions.inst_evh_2576027262358435_close_02e275ta8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2576027262358435_clickPreventStop_5ua0nmhy1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2576027262358435_clickPreventStop_5ua0nmhy1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2576027262358435_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2576027262358435_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7593494570053535_success_2tkb78ja0 = async (event, scope) => {
    return await actions.inst_evh_7593494570053535_success_2tkb78ja0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7593494570053535_clickPreventStop_ic1b80zpb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7593494570053535_clickPreventStop_ic1b80zpb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7593494570053535_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7593494570053535_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_mfj3eeatz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_mfj3eeatz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column">
    <ItpBox>
      <div class="col">
        <ItpText
          strong
          :text="pgettext('RentalServiceCostAllocators', 'Cost allocators')"
        >
        </ItpText>
      </div>
    </ItpBox>
    <ItpBox
      direction="column"
      v-if="service.cost_allocators && service.cost_allocators.data.length"
    >
      <ItpBox :key="item.id" v-for="item in service.cost_allocators.data">
        <UcMeterMiniCard :item="item.meter" class="col-6"> </UcMeterMiniCard>
        <ItpText :text="item.factor" class="col-3"> </ItpText>
        <ItpText :text="item.notes" class="col-3"> </ItpText>
        <BDropdown
          size="sm"
          toggle-class="btn-default-borderless"
          no-caret
          class="ms-auto mb-auto"
          v-if="!item.isReadonly && !service.isReadonly"
        >
          <template #button-content>
            <ItpIcon fa="ellipsis-h" class="mx-1"> </ItpIcon>
          </template>
          <BDropdownItem
            @click.prevent.stop="
              evh_2576027262358435_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
          </BDropdownItem>
          <BDropdownItem
            @click.prevent.stop="
              evh_7593494570053535_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
          </BDropdownItem>
        </BDropdown>
      </ItpBox>
    </ItpBox>
    <ItpBox>
      <div class="col">
        <ItpLink
          :text="
            pgettext('RentalServiceCostAllocators', 'Add cost allocator...')
          "
          class="ms-auto"
          v-if="!service.isReadonly"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpLink>
      </div>
    </ItpBox>
  </ItpBox>
</template>
